import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, Package } from 'lucide-react';
import { ProposalPayload } from '../../types/thread';
import { MilestoneEditor } from '../Contracts/MilestoneEditor';
import { CreateMilestoneInput } from '../../types/contract';
import { briefService } from '../../services/briefService';
import { useToast } from '../UI/Toast';

interface ProposalDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (payload: ProposalPayload) => void;
    type: 'REQUEST' | 'SUBMIT';
    initialData?: ProposalPayload;
    readOnly?: boolean;
    currentUserId?: string;
}

export const ProposalDialog: React.FC<ProposalDialogProps> = ({ isOpen, onClose, onSubmit, type, initialData, readOnly = false, currentUserId }) => {
    const { showToast } = useToast();
    const isRequest = type === 'REQUEST';

    // State
    const [requestMode, setRequestMode] = useState<'FORM' | 'BRIEF'>(initialData?.sourceBriefId ? 'BRIEF' : 'FORM');
    const [selectedBriefId, setSelectedBriefId] = useState<string>(initialData?.sourceBriefId || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [budget, setBudget] = useState<number>(initialData?.budget || 0);

    // Calculate initial delivery days
    const initialDeliveryDays = initialData?.deliveryDate
        ? Math.ceil((initialData.deliveryDate - Date.now()) / (24 * 60 * 60 * 1000))
        : 7;
    const [deliveryDays, setDeliveryDays] = useState<number>(initialDeliveryDays);
    const [milestones, setMilestones] = useState<CreateMilestoneInput[]>(initialData?.milestones || []);
    const [myBriefs, setMyBriefs] = useState<{ id: string; title: string; budget: number }[]>([]);

    // Reset state when dialog opens or initialData changes
    React.useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setRequestMode(initialData.sourceBriefId ? 'BRIEF' : 'FORM');
                setSelectedBriefId(initialData.sourceBriefId || '');
                setDescription(initialData.description || '');
                setBudget(initialData.budget || 0);
                const days = initialData.deliveryDate
                    ? Math.ceil((initialData.deliveryDate - Date.now()) / (24 * 60 * 60 * 1000))
                    : 7;
                setDeliveryDays(days > 0 ? days : 7);
                setMilestones(initialData.milestones || []);
            } else {
                // Reset to defaults
                setRequestMode('FORM');
                setSelectedBriefId('');
                setDescription('');
                setBudget(0);
                setDeliveryDays(7);
                setMilestones([]);
            }
        }
    }, [isOpen, initialData]);

    useEffect(() => {
        if (!isOpen || !isRequest) return;
        briefService.getBriefs({ clientUserId: currentUserId }).then(res => {
            const raw = res.data as any;
            const briefs = raw?.data?.briefs || raw?.briefs || [];
            setMyBriefs(briefs.map((b: any) => ({
                id: b.id || b.briefId,
                title: b.title,
                budget: b.budget || Number(b.budgetRange?.split('-')[1]) || 0,
            })));
        }).catch(() => setMyBriefs([]));
    }, [isOpen, isRequest]);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (requestMode === 'FORM') {
            if (!description.trim()) {
                showToast('请填写描述', 'error');
                return;
            }
            if (!budget || budget <= 0) {
                showToast('请输入有效的金额', 'error');
                return;
            }
            if (!deliveryDays || deliveryDays <= 0) {
                showToast('请输入有效的工期', 'error');
                return;
            }
        }

        if (!isRequest) {
            // Validate Milestones for Proposals
            if (milestones.length === 0) {
                showToast('请至少添加一个里程碑', 'error');
                return;
            }
            for (let i = 0; i < milestones.length; i++) {
                const m = milestones[i];
                if (!m.title.trim() || !m.description.trim() || !m.estimated_completion_date || m.payment_amount <= 0) {
                    showToast(`里程碑 #${i + 1} 信息不完整，请检查标题、描述、日期和金额`, 'error');
                    return;
                }
            }

            // Validate total matches budget (Optional, but good practice. For now just warn or enforce?)
            // Let's just validate fields are present as requested.
        }

        const payload: ProposalPayload = {
            description: requestMode === 'BRIEF' ? `${myBriefs.find(b => b.id === selectedBriefId)?.title}` : description,
            budget: requestMode === 'BRIEF' ? (myBriefs.find(b => b.id === selectedBriefId)?.budget || 0) : budget,
            currency: 'CNY',
            deliveryDate: Date.now() + deliveryDays * 24 * 60 * 60 * 1000,
            milestones: isRequest ? [] : milestones,
            sourceBriefId: requestMode === 'BRIEF' ? selectedBriefId : undefined,
            status: 'PENDING'
        };

        onSubmit(payload);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
                    <h3 className="text-xl font-bold text-gray-900">
                        {readOnly ? '查看方案' : (isRequest ? '发起合作方案请求' : '提交方案')}
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Request Mode Switcher */}
                    {isRequest && (
                        <div className="flex p-1 bg-gray-100 rounded-lg mb-6">
                            <button
                                type="button"
                                onClick={() => setRequestMode('FORM')}
                                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${requestMode === 'FORM' ? 'bg-white shadow-sm text-black' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                填写需求
                            </button>
                            <button
                                type="button"
                                onClick={() => setRequestMode('BRIEF')}
                                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${requestMode === 'BRIEF' ? 'bg-white shadow-sm text-black' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                关联已有需求
                            </button>
                        </div>
                    )}

                    {isRequest && requestMode === 'BRIEF' ? (
                        <div className="space-y-4">
                            <label className="block text-sm font-bold text-gray-700 mb-1">
                                选择需求
                            </label>
                            <div className="grid gap-3">
                                {myBriefs.map(brief => (
                                    <div
                                        key={brief.id}
                                        onClick={() => setSelectedBriefId(brief.id)}
                                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedBriefId === brief.id ? 'border-black bg-gray-50' : 'border-gray-100 hover:border-gray-200'}`}
                                    >
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-gray-900">{brief.title}</span>
                                            <span className="text-sm text-gray-500">¥{brief.budget}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        // Standard Form
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">
                                    {isRequest ? '需求描述' : '方案描述'} {!readOnly && <span className="text-red-500">*</span>}
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={4}
                                    disabled={readOnly}
                                    className="w-full p-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
                                    placeholder={isRequest ? "请描述您的具体需求..." : "请描述您可以提供的服务内容..."}
                                    required={requestMode === 'FORM'}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">
                                        {isRequest ? '预算范围 (元)' : '报价金额 (元)'} {!readOnly && <span className="text-red-500">*</span>}
                                    </label>
                                    <div className="relative">
                                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="number"
                                            disabled={readOnly}
                                            value={budget || ''}
                                            onChange={(e) => setBudget(parseFloat(e.target.value))}
                                            className="w-full pl-9 p-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
                                            placeholder="0.00"
                                            required={requestMode === 'FORM'}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">
                                        预计工期 (天) {!readOnly && <span className="text-red-500">*</span>}
                                    </label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="number"
                                            disabled={readOnly}
                                            value={deliveryDays || ''}
                                            onChange={(e) => setDeliveryDays(parseInt(e.target.value))}
                                            className="w-full pl-9 p-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
                                            placeholder="7"
                                            required={requestMode === 'FORM'}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Milestones (Only for Submit) */}
                    {!isRequest && (!readOnly || milestones.length > 0) && (
                        <div className="border-t border-gray-100 pt-6">
                            <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                里程碑规划
                            </h4>
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <MilestoneEditor
                                    milestones={milestones}
                                    onChange={setMilestones}
                                    readOnly={readOnly}
                                    currency="CNY"
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                        >
                            {readOnly ? '关闭' : '取消'}
                        </button>
                        {!readOnly && (
                            <button
                                type="submit"
                                disabled={isRequest && requestMode === 'BRIEF' && !selectedBriefId}
                                className="px-8 py-2.5 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-colors shadow-lg shadow-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isRequest ? '发送请求' : '提交方案'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};
